// Config for destination and category landing pages.

const collectionLd = (name, desc) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name,
  description: desc,
});

const destinationLd = (name, desc) => ({
  "@context": "https://schema.org",
  "@type": "TouristDestination",
  name,
  description: desc,
});

export const DESTINATIONS = {
  grenada: {
    path: "/grenada",
    seoTitle: "Grenada Tours & Excursions | Things to Do in Grenada | Carivisor",
    seoDesc:
      "Discover the best Grenada tours and excursions — spice tours, waterfall hikes, boat trips, snorkeling and island jeep safaris. Book things to do in Grenada with Carivisor.",
    seoKeywords:
      "Grenada tours and excursions, things to do in Grenada, Grenada boat trips, Grenada waterfall hike, Grenada spice tour, Grenada ATV safari, Caribbean tours and experiences",
    heroImage:
      "https://images.unsplash.com/photo-1566159269137-ccf6e35a126e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxHcmVuYWRhJTIwQmFyYmFkb3MlMjBiZWFjaCUyMHRyb3BpY2FsfGVufDB8fHx8MTc4OTMzODcwNnww&ixlib=rb-4.1.0&q=85",
    eyebrow: "Destination · The Spice Isle",
    title: "Grenada tours &",
    titleAccent: "excursions",
    subtitle:
      "Waterfalls, spice estates, hidden reefs and rainforest ridges — discover the very best things to do in Grenada, all bookable in one place.",
    highlights: ["Spice tours", "Waterfall hikes", "Boat & snorkel trips", "Island jeep safaris"],
    island: "Grenada",
    intro: [
      "Known as the Spice Isle, Grenada packs an extraordinary range of experiences into one small, welcoming island. Wander the fragrant nutmeg and cocoa estates of St. George's, trek through Grand Etang rainforest to the Seven Sisters waterfalls, or cruise the south coast to the world-famous Underwater Sculpture Park.",
      "Whether you're arriving on a cruise, staying at a resort, or exploring independently, Carivisor connects you with locally operated Grenada tours and excursions so you can experience far more than the beach.",
    ],
    jsonLd: destinationLd("Grenada", "Tours, excursions and things to do in Grenada, the Spice Isle of the Caribbean."),
    emptyText: "Grenada experiences are being added. Explore the full marketplace in the meantime.",
  },
  barbados: {
    path: "/barbados",
    seoTitle: "Barbados Tours & Excursions | Things to Do in Barbados | Carivisor",
    seoDesc:
      "Find the best Barbados tours, activities and excursions — coastal buggy rides, rum and food walks, boat trips and more. Book things to do in Barbados with Carivisor.",
    seoKeywords:
      "Barbados tours, things to do in Barbados, Barbados excursions, Barbados buggy ride, Bridgetown rum tour, Barbados activities, Caribbean tours and experiences",
    heroImage:
      "https://images.unsplash.com/photo-1636728163078-59ea0afb7665?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxHcmVuYWRhJTIwQmFyYmFkb3MlMjBiZWFjaCUyMHRyb3BpY2FsfGVufDB8fHx8MTc4OTMzODcwNnww&ixlib=rb-4.1.0&q=85",
    eyebrow: "Destination · Barbados",
    title: "Barbados tours &",
    titleAccent: "experiences",
    subtitle:
      "From the wild Atlantic east coast to historic Bridgetown, discover activities and excursions that help you see far more than just your resort.",
    highlights: ["Coastal buggy rides", "Rum & food walks", "UNESCO old town", "Beach experiences"],
    island: "Barbados",
    intro: [
      "Barbados blends British-Caribbean heritage with rugged surf coasts and some of the friendliest towns in the region. Kick up sand along the dramatic east coast in a dune buggy, or taste your way through the UNESCO-listed streets of Bridgetown on a rum and street-food walk.",
      "Carivisor helps you discover locally run Barbados tours and experiences before you arrive or while you're on the island, so no day of your vacation goes to waste.",
    ],
    jsonLd: destinationLd("Barbados", "Tours, activities and things to do in Barbados, Caribbean."),
    emptyText: "More Barbados experiences are on the way. Explore the full marketplace in the meantime.",
  },
  "st-vincent": {
    path: "/st-vincent",
    seoTitle: "St. Vincent & the Grenadines Tours & Excursions | Carivisor",
    seoDesc:
      "Explore St. Vincent and the Grenadines tours — Tobago Cays sailing, Bequia island hopping, snorkeling and La Soufrière volcano treks. Book Caribbean excursions with Carivisor.",
    seoKeywords:
      "St. Vincent tours, St Vincent and the Grenadines excursions, Tobago Cays sailing, Bequia island hopping, things to do in St Vincent, Caribbean sailing tours",
    heroImage:
      "https://images.unsplash.com/photo-1579167149678-76415f24b197?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxTdCUyMFZpbmNlbnQlMjBHcmVuYWRpbmVzJTIwc2FpbGluZyUyMGlzbGFuZHN8ZW58MHx8fHwxNzg5MzM5MjY2fDA&ixlib=rb-4.1.0&q=85",
    eyebrow: "Destination · St. Vincent & the Grenadines",
    title: "St. Vincent & the Grenadines",
    titleAccent: "tours",
    subtitle:
      "A sailor's paradise of unspoiled islands, coral cays and volcanic peaks. Discover marine adventures, island hopping and locally operated tours across the Grenadines.",
    highlights: ["Tobago Cays sailing", "Bequia island hopping", "Snorkel with turtles", "Volcano treks"],
    island: "St. Vincent and the Grenadines",
    intro: [
      "St. Vincent and the Grenadines is one of the Caribbean's most beautiful sailing grounds — a scattered chain of 32 islands and cays ringed by coral reef. Snorkel with sea turtles in the protected Tobago Cays, hop across to laid-back Bequia, or trek the rainforest slopes of the active La Soufrière volcano.",
      "Carivisor connects travelers with the local guides and boat operators who know these waters best, making it easy to plan marine and island experiences before and during your trip.",
    ],
    jsonLd: destinationLd(
      "St. Vincent and the Grenadines",
      "Sailing, snorkeling and island tours in St. Vincent and the Grenadines."
    ),
    emptyText: "More Grenadines experiences are coming soon. Explore the full marketplace in the meantime.",
  },
  "saint-lucia": {
    path: "/saint-lucia",
    seoTitle: "Saint Lucia Tours & Excursions | Things to Do in St. Lucia | Carivisor",
    seoDesc:
      "Discover the best Saint Lucia tours and excursions — Pitons treks, Sulphur Springs mud baths, Marigot Bay sunset sails and reef snorkels. Book things to do in St. Lucia with Carivisor.",
    seoKeywords:
      "Saint Lucia tours, St Lucia excursions, things to do in Saint Lucia, Pitons trek, Sulphur Springs mud bath, Marigot Bay cruise, Caribbean tours and experiences",
    heroImage:
      "https://images.unsplash.com/photo-1519830103773-4bdc5166a0db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHx0cm9waWNhbCUyMHJhaW5mb3Jlc3QlMjB3YXRlcmZhbGwlMjBoaWtlfGVufDB8fHx8MTc4OTMzMzk0OXww&ixlib=rb-4.1.0&q=85",
    eyebrow: "Destination · Saint Lucia",
    title: "Saint Lucia tours &",
    titleAccent: "excursions",
    subtitle:
      "The iconic Pitons, volcanic mud baths, hidden waterfalls and sunset sails — discover the best things to do in Saint Lucia, all in one place.",
    highlights: ["Pitons treks", "Sulphur Springs mud bath", "Marigot Bay sunset sail", "Reef snorkels"],
    island: "Saint Lucia",
    intro: [
      "Few Caribbean islands are as instantly recognisable as Saint Lucia, crowned by the twin volcanic spires of the Pitons. Hike lush rainforest to hidden waterfalls, soak in the mineral mud of the world's only drive-in volcano, or sail the dramatic west coast at golden hour.",
      "Carivisor connects you with locally operated Saint Lucia tours and excursions so you can experience the island's raw natural beauty beyond the resort.",
    ],
    jsonLd: destinationLd("Saint Lucia", "Tours, excursions and things to do in Saint Lucia, Caribbean."),
    emptyText: "More Saint Lucia experiences are on the way. Explore the full marketplace in the meantime.",
  },
  "turks-caicos": {
    path: "/turks-caicos",
    seoTitle: "Turks & Caicos Tours & Excursions | Grace Bay & Cays | Carivisor",
    seoDesc:
      "Discover Turks & Caicos tours and excursions — Grace Bay reef snorkels, sunset catamaran cruises and uninhabited cay boat tours. Book things to do in Turks & Caicos with Carivisor.",
    seoKeywords:
      "Turks and Caicos tours, Turks & Caicos excursions, things to do in Turks and Caicos, Grace Bay snorkel, Providenciales boat tour, Caribbean tours and experiences",
    heroImage:
      "https://images.unsplash.com/photo-1769610352818-cf8fa29ccf9a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB5YWNodCUyMGNhdGFtYXJhbiUyMGNhcmliYmVhbiUyMHN1bnNldHxlbnwwfHx8fDE3ODkyNzA3MDR8MA&ixlib=rb-4.1.0&q=85",
    eyebrow: "Destination · Turks & Caicos",
    title: "Turks & Caicos tours &",
    titleAccent: "excursions",
    subtitle:
      "World-famous Grace Bay, glass-clear reefs and uninhabited cays. Discover snorkel safaris, sunset catamaran cruises and island-hopping boat tours.",
    highlights: ["Grace Bay reef snorkel", "Sunset catamaran", "Rock iguana cays", "Sandbar stops"],
    island: "Turks & Caicos",
    intro: [
      "Home to Grace Bay — regularly voted the world's best beach — Turks & Caicos is a dream for anyone who loves the water. Snorkel pristine coral gardens, cruise to uninhabited cays to meet the rare rock iguanas, or sail into a flawless Providenciales sunset.",
      "Carivisor helps you discover locally operated Turks & Caicos excursions before you arrive or while you're on the island.",
    ],
    jsonLd: destinationLd("Turks and Caicos", "Tours, excursions and things to do in Turks & Caicos, Caribbean."),
    emptyText: "More Turks & Caicos experiences are on the way. Explore the full marketplace in the meantime.",
  },
};

export const CATEGORIES = {
  "atv-tours": {
    path: "/atv-tours",
    seoTitle: "Caribbean ATV & Off-Road Tours | Jeep & Buggy Adventures | Carivisor",
    seoDesc:
      "Book Caribbean ATV tours, jeep safaris and off-road buggy adventures. Explore island trails, crater lakes and rugged coastlines with local operators on Carivisor.",
    seoKeywords:
      "Caribbean ATV tours, ATV adventures Caribbean, jeep safari Caribbean, off-road buggy tour, Grenada jeep safari, Barbados buggy ride, Caribbean adventure tours",
    heroImage:
      "https://images.unsplash.com/photo-1496521061024-90e1c1221555?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxBVFYlMjBvZmYlMjByb2FkJTIwdHJvcGljYWwlMjBhZHZlbnR1cmV8ZW58MHx8fHwxNzg5MzM4NzA2fDA&ixlib=rb-4.1.0&q=85",
    eyebrow: "Category · Adventure",
    title: "Caribbean ATV &",
    titleAccent: "off-road tours",
    subtitle:
      "Kick up dust on island trails, splash through rivers and reach viewpoints most visitors never see — on 4x4 jeeps, dune buggies and off-road adventures.",
    highlights: ["4x4 jeep safaris", "Dune buggies", "Mountain trails", "Hotel pickup"],
    keywords: ["atv", "jeep", "buggy", "safari", "4x4", "off-road", "offroad", "quad", "dune", "rides"],
    intro: [
      "Nothing gets your adrenaline going like an off-road Caribbean adventure. Carivisor's ATV, jeep and buggy tours take you deep into island interiors — crater lakes, rum distilleries, rainforest ridges and rugged Atlantic coastlines — with experienced local driver-guides.",
      "Perfect for thrill-seekers, families and groups looking to swap the sun-lounger for something unforgettable.",
    ],
    jsonLd: collectionLd("Caribbean ATV & Off-Road Tours", "ATV, jeep and buggy adventure tours across the Caribbean."),
  },
  "boat-tours": {
    path: "/boat-tours",
    seoTitle: "Caribbean Boat Tours & Sailing Trips | Catamaran & Yacht | Carivisor",
    seoDesc:
      "Discover Caribbean boat tours, catamaran cruises, sailing trips and private yacht charters. Snorkel reefs, chase sunsets and island-hop with local operators on Carivisor.",
    seoKeywords:
      "Caribbean boat tours, catamaran cruise Caribbean, Caribbean sailing trips, private yacht charter Caribbean, sunset cruise, snorkel boat trip, Caribbean tours and experiences",
    heroImage:
      "https://images.unsplash.com/photo-1780839955296-55174f52f7da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxjYXRhbWFyYW4lMjBzYWlsaW5nJTIwdHVycXVvaXNlJTIwc2VhfGVufDB8fHx8MTc4OTMzOTI2Nnww&ixlib=rb-4.1.0&q=85",
    eyebrow: "Category · On the Water",
    title: "Caribbean boat tours &",
    titleAccent: "sailing trips",
    subtitle:
      "Catamaran cruises, sunset sails, private yacht days and island-hopping adventures across the region's most beautiful turquoise waters.",
    highlights: ["Catamaran cruises", "Sunset sails", "Private yacht days", "Island hopping"],
    keywords: ["boat", "catamaran", "yacht", "sail", "sailing", "cruise", "charter", "hop", "sunset", "grenadines"],
    intro: [
      "The best way to experience the Caribbean is from the water. Carivisor's boat tours range from lively catamaran day-cruises with open bars to intimate private yacht charters and traditional island-hopping trips through the Grenadines.",
      "Snorkel over coral reefs, drift into neon sunsets, and discover secluded coves you simply can't reach by land.",
    ],
    jsonLd: collectionLd("Caribbean Boat Tours & Sailing Trips", "Catamaran, sailing and yacht boat tours across the Caribbean."),
  },
  "shore-excursions": {
    path: "/shore-excursions",
    seoTitle: "Caribbean Cruise Shore Excursions | Port Day Tours | Carivisor",
    seoDesc:
      "Make the most of your cruise port day with Caribbean shore excursions — boat trips, sightseeing, snorkeling and island tours timed for cruise passengers. Book on Carivisor.",
    seoKeywords:
      "Caribbean shore excursions, cruise shore excursions, cruise port day tours, cruise excursions Grenada, cruise excursions Barbados, things to do cruise Caribbean",
    heroImage:
      "https://images.unsplash.com/photo-1717465760671-c47cb5f8b56f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxjcnVpc2UlMjBzaGlwJTIwY2FyaWJiZWFuJTIwcG9ydHxlbnwwfHx8fDE3ODkzMzkyNjZ8MA&ixlib=rb-4.1.0&q=85",
    eyebrow: "Category · Cruise",
    title: "Caribbean cruise",
    titleAccent: "shore excursions",
    subtitle:
      "Only have a few hours ashore? Discover shore excursions and half-day experiences timed for cruise passengers, so you can explore beyond the port and be back on board in time.",
    highlights: ["Half-day trips", "Near cruise ports", "Small groups", "Back in time"],
    keywords: ["boat", "catamaran", "snorkel", "reef", "spice", "tour", "hop", "sailing", "sunset", "sightseeing"],
    intro: [
      "A cruise port day is precious — and Carivisor helps you spend it wisely. Our shore excursions and shorter experiences are ideal for the limited hours you have on the island, from catamaran snorkel trips to spice tours and city sightseeing.",
      "Skip the crowded ship-arranged tours and book directly with the local operators who make each Caribbean destination special.",
    ],
    jsonLd: collectionLd("Caribbean Cruise Shore Excursions", "Shore excursions and port-day tours for Caribbean cruise passengers."),
  },
  "water-activities": {
    path: "/water-activities",
    seoTitle: "Caribbean Water Activities | Snorkeling, Swimming & Reef Tours | Carivisor",
    seoDesc:
      "Book Caribbean water activities — snorkeling, reef tours, waterfall swims and marine adventures. Discover things to do on and in the water with local operators on Carivisor.",
    seoKeywords:
      "Caribbean water activities, Caribbean snorkeling tours, reef snorkel Caribbean, waterfall swim, marine experiences Caribbean, Caribbean water sports, things to do in the Caribbean",
    heroImage:
      "https://images.unsplash.com/photo-1583364493238-248032147fbd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxzbm9ya2VsaW5nJTIwY29yYWwlMjByZWVmJTIwdW5kZXJ3YXRlcnxlbnwwfHx8fDE3ODkzMzkyNjZ8MA&ixlib=rb-4.1.0&q=85",
    eyebrow: "Category · In the Water",
    title: "Caribbean",
    titleAccent: "water activities",
    subtitle:
      "Snorkel vibrant reefs, swim beneath rainforest waterfalls and dive into marine parks teeming with turtles and tropical fish across the Caribbean.",
    highlights: ["Reef snorkeling", "Waterfall swims", "Marine parks", "All levels welcome"],
    keywords: ["snorkel", "reef", "swim", "dive", "water", "waterfall", "kayak", "surf", "catamaran", "boat", "marine", "underwater"],
    intro: [
      "The Caribbean's warm, glassy waters are made for adventure. Carivisor's water activities span protected reef snorkels, marine-park excursions, waterfall swims and beach experiences suited to every level — from first-timers to seasoned divers.",
      "Grab your fins and discover the technicolour world beneath the surface with trusted local guides.",
    ],
    jsonLd: collectionLd("Caribbean Water Activities", "Snorkeling, reef tours and marine water activities across the Caribbean."),
  },
};
